
import axios from 'axios';
import { AppointmentStatus } from '../types/enums';

import { getAuthToken } from '../utils/authUtils'; //

const API_BASE = process.env.REACT_APP_FIREBASE_BACKEND_API_BASE || 'https://quantumleap-emr-dev.web.app';
export const verifyAppointment = async (identifier) => {
  try {
    const token = await getAuthToken();
    
    const response = await axios.post(
      `${API_BASE}/internal/appointments/verify`,
      {
        identifier: identifier.toLowerCase()
      },
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    return response.data;
  } catch (error) {
    console.error('Error verifying appointment:', error);
    if (axios.isAxiosError(error) && error.response?.status === 404) {
      return {
        success: false,
        message: 'No scheduled appointment found within next hour'
      };
    }
    throw new Error('Failed to verify appointment');
  }
};

// Update appointment status - uses secure backend API
export const updateAppointmentStatus = async (appointmentId, newStatus, additionalData = {}) => {
  try {
    const token = await getAuthToken();
    
    const updateData = {
      status: newStatus,
      ...additionalData
    };
    
    // Add checked-in timestamp if status is being changed to checked-in
    if (newStatus === AppointmentStatus.CHECKED_IN) {
      updateData.checkedInAt = new Date().toISOString();
    }
    
    await axios.put(
      `${API_BASE}/internal/appointments/${appointmentId}`,
      updateData,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    return {
      success: true,
      appointmentId,
      newStatus,
      message: `Appointment status updated to ${newStatus}`
    };
  } catch (error) {
    console.error('Error updating appointment status:', error);
    throw new Error('Failed to update appointment status');
  }
};

// Update appointment data - for general appointment updates
export const updateAppointment = async (appointmentId, updateData) => {
  try {
    const token = await getAuthToken();
    
    // Convert date to ISO string if it exists
    const dataToUpdate = { ...updateData };
    if (dataToUpdate.date) {
      dataToUpdate.date = new Date(dataToUpdate.date).toISOString();
    }
    
    await axios.put(
      `${API_BASE}/internal/appointments/${appointmentId}`,
      dataToUpdate,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    return {
      success: true,
      appointmentId,
      message: 'Appointment updated successfully'
    };
  } catch (error) {
    console.error('Error updating appointment:', error);
    throw new Error('Failed to update appointment');
  }
};

// Get appointment by ID
export const getAppointmentById = async (appointmentId) => {
  try {
    const token = await getAuthToken();
    
    const response = await axios.get(
      `${API_BASE}/internal/appointments/${appointmentId}`,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    return response.data.appointment;
  } catch (error) {
    console.error('Error getting appointment:', error);
    if (axios.isAxiosError(error) && error.response?.status === 404) {
      throw new Error('Appointment not found');
    }
    throw new Error('Failed to get appointment');
  }
};

export const createAppointmentInDb = async (appointmentData) => {
  try {
    const token = await getAuthToken();
    
    // Convert date to ISO string if it exists
    const dataToCreate = { ...appointmentData };
    if (dataToCreate.date) {
      dataToCreate.date = new Date(dataToCreate.date).toISOString();
    }
    
    const response = await axios.post(
      `${API_BASE}/internal/appointments`,
      dataToCreate,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    return response.data;
  } catch (error) {
    console.error('Error creating appointment:', error);
    throw new Error('Failed to create appointment');
  }
};

// Get appointments with filters
export const getAppointments = async (filters = {}) => {
  try {
    const token = await getAuthToken();
    const params = new URLSearchParams();
    
    // Add filters as query parameters
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        params.append(key, value.toString());
      }
    });
    
    const response = await axios.get(
      `${API_BASE}/internal/appointments?${params.toString()}`,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    return response.data;
  } catch (error) {
    console.error('Error getting appointments:', error);
    throw new Error('Failed to get appointments');
  }
}; 

// Get appointments by facility with filtering
export const getAppointmentsByFacility = async (facilityId, filters = {}) => {
  try {
    const token = await getAuthToken();
    
    const params = new URLSearchParams();
    params.append('facilityId', facilityId);
    
    if (filters.startDate) params.append('startDate', filters.startDate);
    if (filters.endDate) params.append('endDate', filters.endDate);
    if (filters.statusFilter && filters.statusFilter !== 'all') params.append('status', filters.statusFilter);
    
    const response = await axios.get(
      `${API_BASE}/internal/appointments?${params.toString()}`,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    return response.data.appointments || [];
  } catch (error) {
    console.error('Error fetching appointments by facility:', error);
    throw new Error('Failed to fetch appointments');
  }
};

// Get patients by facility
export const getPatientsByFacility = async (facilityId) => {
  try {
    const token = await getAuthToken();
    
    const response = await axios.get(
      `${API_BASE}/clinical/facilities/${facilityId}/patients`,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    return response.data.patients || [];
  } catch (error) {
    console.error('Error fetching patients by facility:', error);
    throw new Error('Failed to fetch patients');
  }
};

// Get appointments by facility and date range for timeline view
export const getAppointmentsForTimeline = async (facilityId, startDate, endDate) => {
  try {
    const token = await getAuthToken();
    
    const params = new URLSearchParams();
    params.append('facilityId', facilityId);
    params.append('startDate', startDate.toISOString());
    params.append('endDate', endDate.toISOString());
    
    const response = await axios.get(
      `${API_BASE}/internal/appointments?${params.toString()}`,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    return response.data.appointments || [];
  } catch (error) {
    console.error('Error fetching appointments for timeline:', error);
    return [];
  }
}; 